/**
 * `/internal/email-notices*` and `/internal/email-templates*` wire contract —
 * operator-authored customer mail (#1911, parent #1875).
 *
 * ── WHAT THIS REPLACED, AND WHY ─────────────────────────────────────────────
 * #1877 shipped this as "model shelf notices", where one entity decided three
 * things at once: what the mail said, who received it, and when a model's shelf
 * flipped. Each answer constrained the others. The kind was INFERRED from the
 * first selected model's shelf status, so an operator could not say what they
 * were announcing; the audience was two different queries chosen by that kind;
 * and the body was assembled by the template from a headline, a model list and
 * a replacement note, so the operator wrote a paragraph in the middle of a
 * sentence somebody else had written.
 *
 * The three are separated here. The shelf flip is gone entirely — it lives on
 * the SKU detail page behind a readiness checklist (pricing, supply, routing,
 * cost), and the notice's copy of it had none of those gates. `remind_on` is
 * all that remains of the schedule, and it drives NOTHING: no status moves, no
 * model changes, a card gets posted and that is all.
 *
 * The lifecycle is therefore as short as it can be, and every transition is an
 * operator action:
 *
 *   draft ──send──▶ sent
 *     └──── cancel ────▶ cancelled
 *
 * ── THE TWO SEND GATES ARE THE WHOLE SAFETY STORY ───────────────────────────
 * A send is unrecallable and reaches people who pay us. Two things must be true
 * before one is allowed, and both are mechanised in the schema rather than only
 * checked in the route: the recipient list has been FROZEN (so the operator saw
 * and could strike out every address), and a rehearsal has SUCCEEDED (so the
 * channel is known to work and the copy is known to render). Any accepted draft
 * edit clears both, because a list frozen against old copy proves nothing about
 * new copy.
 *
 * The rehearsal used to be required once PER BRAND the frozen list spanned,
 * because each brand was a separate SMTP account behind a separate cold domain.
 * Since #1935 every customer-facing bulk mail leaves as `BULK_EMAIL_BRAND`, so
 * one rehearsal exercises the one sender the blast will use.
 */

import type { BrandId } from './brands.ts';

export type EmailNoticeStatus = 'draft' | 'sent' | 'cancelled';

/**
 * Default lookback for the `models_used` audience, in days.
 *
 * 30 days is the window the usage surfaces already use for "recently active",
 * so an operator reading a recipient count against the usage page sees one
 * population rather than two numbers that need reconciling.
 */
export const EMAIL_NOTICE_DEFAULT_WINDOW_DAYS = 30;

/**
 * Ceiling on the operator-supplied window.
 *
 * The audience scan is a range read over `usage_rated_events` — a money-path
 * table — so an unbounded window is a full-table scan bound to an ops click.
 * 180 days is far beyond any defensible "they will notice" claim and still
 * bounded.
 */
export const EMAIL_NOTICE_MAX_WINDOW_DAYS = 180;

export interface EmailNoticeModelRef {
  readonly vendor: string;
  readonly model: string;
}

/**
 * WHO a notice reaches, as a discriminated union tagged by `type`.
 *
 * ── WHY A UNION AND NOT COLUMNS ─────────────────────────────────────────────
 * Selecting by model is ONE way of drawing an audience and it was never going
 * to be the only one. #1877 hardcoded two mutually exclusive queries and chose
 * between them with a `kind` field that also decided the mail's headline and
 * the shelf target; adding a third way meant touching all three. Here a new
 * strategy costs exactly one union member and one resolver function — no
 * migration, no route change, no new console page.
 *
 * The stored form is `email_notices.audience_json`, whose only database-level
 * rule is "a JSON object carrying a `type`". Everything below is enforced by
 * {@link isNoticeAudience} at the route, where a bad payload can be reported to
 * the operator who typed it rather than silently resolving to nobody.
 */
export type NoticeAudience = {
  /** Accounts that called at least one of `models` within the window. */
  readonly type: 'models_used';
  readonly models: readonly EmailNoticeModelRef[];
  readonly window_days: number;
};

export type NoticeAudienceType = NoticeAudience['type'];

export const NOTICE_AUDIENCE_TYPES: readonly NoticeAudienceType[] = ['models_used'];

export type EmailNoticeRecipientStatus = 'pending' | 'sent' | 'failed';

export interface EmailNoticeRecipient {
  readonly account_id: string;
  readonly email: string;
  readonly brand: BrandId;
  readonly status: EmailNoticeRecipientStatus;
  readonly attempts: number;
  readonly last_error: string | null;
  readonly sent_at: string | null;
}

export interface EmailNotice {
  readonly id: string;
  readonly status: EmailNoticeStatus;
  /**
   * The operator's own label for this job. Never leaves the console — it is
   * what a human scans a list of forty notices by, e.g.
   * "Claude Code Opus4.6 下架通知".
   */
  readonly task_title: string;
  /** The mail's Subject header. This is what a paying customer reads. */
  readonly subject: string;
  /** The mail's body, verbatim. The template escapes it and adds nothing. */
  readonly body: string;
  readonly audience: NoticeAudience;
  /**
   * `YYYY-MM-DD` or null. A REMINDER and nothing else: the daily sweep posts a
   * card when it arrives. No status moves, no model changes. #1877's
   * `effective_date` drove a transition into `pending_execution`; this one has
   * no transition to drive.
   */
  readonly remind_on: string | null;
  /** Set once the sweep has posted this notice's card. */
  readonly reminded_at: string | null;
  readonly created_by_ops_user_id: string;
  readonly created_at: string;
  readonly updated_at: string;
  /** Set when the recipient list was frozen; cleared by any draft edit. */
  readonly recipients_finalized_at: string | null;
  /** Set by the first successful test send. Cleared by any draft edit. */
  readonly test_sent_at: string | null;
  readonly sent_at: string | null;
  readonly cancelled_at: string | null;
}

export interface EmailNoticeDetail {
  readonly notice: EmailNotice;
  readonly recipients: readonly EmailNoticeRecipient[];
  /**
   * `[BULK_EMAIL_BRAND]` once this notice has a confirmed rehearsal, `[]`
   * before that.
   *
   * ── THIS IS EVIDENCE, NOT A VERDICT ─────────────────────────────────────
   * Gate two asks "has the channel this send will use been rehearsed", and
   * that is still a comparison rather than a field:
   *
   *     const gateTwoPassed = proven_brands.includes(BULK_EMAIL_BRAND);
   *
   * `notice.test_sent_at` is not it. The timestamp and the proof rows are
   * cleared by the same accepted draft edit, so today they agree — but the
   * timestamp is a summary and the rows are the evidence, and a console that
   * renders the summary as the verdict is one schema change away from a green
   * gate over a send that 409s.
   *
   * ── WHY IT IS STILL A LIST (#1935) ──────────────────────────────────────
   * Every bulk mail now leaves as one sender, so this can hold at most one
   * entry. The array shape is kept because it is what `/send` and the console
   * name the unproven channel from, and because collapsing it to a boolean
   * would discard which sender was actually rehearsed — the one fact an
   * operator reading an old notice would want.
   *
   * Cleared, with the proof rows themselves, by any accepted draft edit.
   */
  readonly proven_brands: readonly BrandId[];
}

export interface ListEmailNoticesResponse {
  readonly notices: readonly EmailNotice[];
}

export interface CreateEmailNoticeRequest {
  readonly task_title: string;
  readonly subject: string;
  readonly body: string;
  readonly audience: NoticeAudience;
  readonly remind_on?: string | null;
}

export type UpdateEmailNoticeRequest = Partial<CreateEmailNoticeRequest>;

/**
 * Audience preview — computed, never stored, and NOT keyed on a notice (#1920).
 *
 * ── WHY IT TAKES AN AUDIENCE RATHER THAN AN ID ──────────────────────────────
 * "These models, this window — how many accounts is that" is a pure query. It
 * has no dependency on any notice existing, and the first shape of this route
 * (`POST /:id/recipients/preview`) put it behind one anyway: an operator had to
 * invent a title and a body and save a draft before the console would tell them
 * whether the selection reached four people or four hundred. The id bought
 * nothing — the route read the stored audience and immediately discarded the
 * rest of the row.
 *
 * ── THIS DOES NOT LOOSEN THE FREEZE ─────────────────────────────────────────
 * `POST /:id/recipients` still recomputes from the STORED audience rather than
 * accepting rows a client previewed. Preview and freeze run at two different
 * instants, so an account that started calling the model in between appears in
 * the frozen list and in no earlier surface — which is exactly why the frozen
 * table is rendered again before the send. Letting the preview escape the draft
 * must not turn into letting the freeze trust the preview.
 */
export interface PreviewEmailNoticeAudienceRequest {
  readonly audience: NoticeAudience;
}

/**
 * Recipient preview — computed, never stored.
 *
 * Separate from the finalize call because the first send gate (see the list,
 * export it, strike rows out) only works if a recipient set can be asked for
 * BEFORE anything is committed. A preview that wrote rows would make "let me
 * see who this reaches" a mutation.
 */
export interface EmailNoticeRecipientCandidate {
  readonly account_id: string;
  readonly email: string;
  readonly brand: BrandId;
  /** Requests this account made against the audience's scope in the window. */
  readonly request_count: number;
  readonly last_used_at: string;
}

export interface EmailNoticeRecipientPreviewResponse {
  readonly window_days: number;
  readonly window_from: string;
  readonly candidates: readonly EmailNoticeRecipientCandidate[];
}

export interface FinalizeEmailNoticeRecipientsRequest {
  /**
   * Account ids to exclude from the frozen list. Anything not in the computed
   * candidate set is ignored rather than rejected: the console computes the
   * preview and the finalize against two different instants, so a candidate
   * that aged out between them is not an operator error.
   */
  readonly exclude_account_ids?: readonly string[];
}

export interface FinalizeEmailNoticeRecipientsResponse {
  readonly notice: EmailNotice;
  readonly recipients: readonly EmailNoticeRecipient[];
}

/**
 * No `brand` (#1935). The caller does not choose which sender to exercise,
 * because there is one: a rehearsal that could be routed anywhere other than
 * where the blast goes would prove nothing about the sender, the domain or the
 * credential, which is most of what a rehearsal is for.
 */
export interface EmailNoticeTestSendRequest {
  readonly to: string;
}

export interface EmailNoticeTestSendResponse {
  readonly notice: EmailNotice;
  readonly to: string;
  /** The sender this call proved — always `BULK_EMAIL_BRAND`. */
  readonly brand: BrandId;
  /** Proven for this notice so far, this call included. */
  readonly proven_brands: readonly BrandId[];
  /**
   * Non-empty while the channel this send needs has no proof. `/send` is
   * refused while it is, so it is the operator's remaining work — surfaced
   * here rather than left for the refusal to reveal.
   */
  readonly pending_brands: readonly BrandId[];
}

export interface EmailNoticeSendResponse {
  readonly notice: EmailNotice;
  /** Addresses this call delivered to. Excludes already-`sent` rows. */
  readonly sent: number;
  readonly failed: number;
  /** Rows left untouched because the pass ran out of its wall-clock budget. */
  readonly deferred: number;
  readonly recipients: readonly EmailNoticeRecipient[];
}

export interface EmailNoticeReminderEntry {
  readonly notice_id: string;
  readonly task_title: string;
  readonly remind_on: string;
}

export interface EmailNoticeReminderResponse {
  readonly ok: boolean;
  readonly environment: string;
  readonly checked: number;
  /** Notices whose card this pass posted and stamped `reminded_at`. */
  readonly reminded: readonly EmailNoticeReminderEntry[];
  /**
   * Whether a Lark URL was configured at all. `sendLarkNotification` collapses
   * "no URL" and "delivery failed" into one `false`, so without this a
   * misconfigured environment reports an empty sweep and reads like a quiet
   * calendar forever.
   */
  readonly webhook_configured: boolean;
  /**
   * Due notices whose card was NOT confirmed delivered. Each one was left
   * unstamped and will be retried by the next pass.
   *
   * IT ANSWERS A DIFFERENT QUESTION FROM {@link ok}, which is why both exist.
   * `ok` is the pass's verdict — the thing `cron_run_history` is scanned for
   * and the thing an alarm keys on. This is the magnitude: one dead card among
   * five is a blip, five among five is an outage, and `ok` alone cannot tell
   * them apart.
   */
  readonly delivery_failures: number;
  readonly error?: string;
}

/**
 * Whole-pass wall clock for `POST /internal/email-notices/:id/send`.
 *
 * The pass mails serially and each address is one SMTP conversation, so the
 * bound has to be a CLOCK rather than a count: a provider that has gone slow
 * without failing would otherwise hold the request open until the client gives
 * up mid-send, which is the one condition under which "how many went out" is
 * unknowable. Rows past the budget are left `pending` and reported as
 * `deferred`; the operator presses send again and the `(notice_id, account_id)`
 * primary key makes the second press skip everything already delivered.
 */
export const EMAIL_NOTICE_SEND_BUDGET_MS = 45_000;

// ---------------------------------------------------------------------------
// Editable mail copy
// ---------------------------------------------------------------------------

/**
 * The fixed set of template keys. Rows are seeded by migrations 0033 (balance
 * / notice) and 0052 (long-term credit) and can be EDITED but not created or
 * deleted: `balance_alert.critical` is not a row an operator should be able to
 * remove, because the sender reaches for it at a moment when nobody is watching.
 *
 * The `notice.*` pair are starting drafts for the manual surface rather than
 * messages the platform sends by itself. They live here instead of as console
 * constants so that changing the wording of a customer mail is the same act
 * wherever it happens — and so a future locale variant has one place to go.
 */
export const EMAIL_TEMPLATE_KEYS = [
  'balance_alert.warning',
  'balance_alert.critical',
  'balance_alert.recovered',
  'notice.delist',
  'notice.list',
  'credit.due_soon_7d',
  'credit.due_soon_3d',
  'credit.due_today',
  'credit.frozen',
  'credit.overdue_reminder',
  'credit.stopped',
  'credit.restored',
  'credit.temp_expiring_3d',
  'credit.temp_expiring_1d',
  'codex_capacity.elevated',
  'codex_capacity.recovered',
] as const;

export type EmailTemplateKey = (typeof EMAIL_TEMPLATE_KEYS)[number];

export interface EmailTemplate {
  readonly key: EmailTemplateKey;
  readonly subject: string;
  readonly body: string;
  readonly updated_by_ops_user_id: string | null;
  readonly updated_at: string;
}

export interface ListEmailTemplatesResponse {
  readonly templates: readonly EmailTemplate[];
}

export interface UpdateEmailTemplateRequest {
  readonly subject: string;
  readonly body: string;
}

/** No `brand` — see {@link EmailNoticeTestSendRequest} (#1935). */
export interface EmailTemplateTestSendRequest {
  readonly to: string;
}

export interface EmailTemplateTestSendResponse {
  readonly key: EmailTemplateKey;
  readonly to: string;
  /** The sender this call proved — always `BULK_EMAIL_BRAND`. */
  readonly brand: BrandId;
}

/**
 * Placeholders each template key may use, and no others.
 *
 * ── WHY AN ALLOWLIST, CHECKED AT SAVE TIME ──────────────────────────────────
 * A placeholder is substituted at send time, and a send is unrecallable. A
 * mistyped `{{ balnce }}` that is accepted here reaches a customer's inbox with
 * the braces still in it — there is no send-time check that can undo that, and
 * no plausible reading of "render what you were given" that produces a good
 * outcome. So the refusal happens in front of the operator who typed it, where
 * the fix costs one keystroke.
 *
 * ── `days_left` IS DELIBERATELY ABSENT ──────────────────────────────────────
 * The balance alert's runway is nullable, and null does not mean "unlimited" —
 * it means the trailing window saw no attributed usage, which is ALSO the shape
 * a stalled rating pipeline produces. Exposing the raw number would let an
 * operator write "you have {{ days_left }} days left" and mail a customer a
 * blank in exactly the case the sender is least sure of. `runway` is the
 * pre-composed phrase that renders that case honestly, and it is the only way
 * to reach the value.
 *
 * ── NO URL PLACEHOLDER ──────────────────────────────────────────────────────
 * #1876 rendered a hardcoded "Add funds" button into the alert's HTML. It is
 * gone with the rest of the fixed layout: the body is the operator's prose now,
 * and a link they did not write is not prose.
 */
const CREDIT_MAIL_BASE_PLACEHOLDERS = ['brand', 'name', 'email', 'debt', 'due_date'] as const;
const CREDIT_MAIL_DUE_SOON_PLACEHOLDERS = [
  ...CREDIT_MAIL_BASE_PLACEHOLDERS,
  'days_until_due',
] as const;
const CREDIT_MAIL_OVERDUE_PLACEHOLDERS = [
  ...CREDIT_MAIL_BASE_PLACEHOLDERS,
  'days_past_due',
] as const;

export const EMAIL_TEMPLATE_PLACEHOLDERS: Readonly<
  Record<EmailTemplateKey, readonly string[]>
> = {
  'balance_alert.warning': ['brand', 'name', 'email', 'balance', 'runway'],
  'balance_alert.critical': ['brand', 'name', 'email', 'balance', 'runway'],
  'balance_alert.recovered': ['brand', 'name', 'email', 'balance', 'runway'],
  'notice.delist': ['brand', 'name', 'email'],
  'notice.list': ['brand', 'name', 'email'],
  'credit.due_soon_7d': CREDIT_MAIL_DUE_SOON_PLACEHOLDERS,
  'credit.due_soon_3d': CREDIT_MAIL_DUE_SOON_PLACEHOLDERS,
  'credit.due_today': CREDIT_MAIL_BASE_PLACEHOLDERS,
  'credit.frozen': CREDIT_MAIL_OVERDUE_PLACEHOLDERS,
  'credit.overdue_reminder': CREDIT_MAIL_OVERDUE_PLACEHOLDERS,
  'credit.stopped': CREDIT_MAIL_OVERDUE_PLACEHOLDERS,
  'credit.restored': CREDIT_MAIL_BASE_PLACEHOLDERS,
  'credit.temp_expiring_3d': CREDIT_MAIL_DUE_SOON_PLACEHOLDERS,
  'credit.temp_expiring_1d': CREDIT_MAIL_DUE_SOON_PLACEHOLDERS,
  'codex_capacity.elevated': ['brand', 'name', 'email'],
  'codex_capacity.recovered': ['brand', 'name', 'email'],
};

/**
 * Outbox event type → seeded `email_templates` key for credit customer
 * mail (router#2044, router#2074).
 *
 * `temporary_credit_expiring` stays in this map so an outbox row is
 * not treated as unmapped. Delivery itself does not consume that row:
 * T-3 / T-1 letters are scanned from active grants with independent
 * keys, the same calendar as the Feishu kinds, not a second set of
 * constants.
 */
export const CREDIT_CUSTOMER_MAIL_TEMPLATE_BY_EVENT = {
  credit_due_soon_7d: 'credit.due_soon_7d',
  credit_due_soon_3d: 'credit.due_soon_3d',
  credit_due_today: 'credit.due_today',
  credit_overdue_frozen: 'credit.frozen',
  credit_overdue_reminder: 'credit.overdue_reminder',
  credit_service_stopped: 'credit.stopped',
  credit_overdue_cleared: 'credit.restored',
  temporary_credit_expiring: 'credit.temp_expiring_3d',
} as const;

export type CreditCustomerMailEventType = keyof typeof CREDIT_CUSTOMER_MAIL_TEMPLATE_BY_EVENT;

export function isCreditCustomerMailEventType(
  value: string,
): value is CreditCustomerMailEventType {
  return value in CREDIT_CUSTOMER_MAIL_TEMPLATE_BY_EVENT;
}

/**
 * Placeholders a manual notice's own subject and body may use.
 *
 * Deliberately the same set as `notice.*` above: a notice starts from one of
 * those templates and is then edited freely, so a placeholder that was legal in
 * the starting draft must stay legal in the result. Two different sets would
 * make "load the template, save it unchanged" a validation error.
 */
export const EMAIL_NOTICE_PLACEHOLDERS: readonly string[] = ['brand', 'name', 'email'];

/** `{{ name }}`, `{{name}}`, `{{  name  }}` — all one placeholder. */
const PLACEHOLDER_PATTERN = /\{\{\s*([a-z_][a-z0-9_]*)\s*\}\}/gi;

/** Every distinct placeholder name appearing in `text`, in first-seen order. */
export function extractPlaceholders(text: string): readonly string[] {
  const seen: string[] = [];
  for (const match of text.matchAll(PLACEHOLDER_PATTERN)) {
    const name = match[1]?.toLowerCase();
    if (name !== undefined && !seen.includes(name)) seen.push(name);
  }
  return seen;
}

/**
 * Placeholders used by `subject` + `body` that are not in `allowed`.
 *
 * Empty means the copy is safe to store. Non-empty is reported to the operator
 * verbatim, because "which word did I get wrong" is the only question they have
 * at that moment.
 */
export function unknownPlaceholders(
  subject: string,
  body: string,
  allowed: readonly string[],
): readonly string[] {
  const used = [...extractPlaceholders(subject), ...extractPlaceholders(body)];
  const allowedSet = new Set(allowed);
  const bad: string[] = [];
  for (const name of used) {
    if (!allowedSet.has(name) && !bad.includes(name)) bad.push(name);
  }
  return bad;
}

export function isEmailTemplateKey(value: unknown): value is EmailTemplateKey {
  return typeof value === 'string'
    && (EMAIL_TEMPLATE_KEYS as readonly string[]).includes(value);
}

/**
 * Structural validation of a stored/submitted audience.
 *
 * The window bound is checked HERE rather than only at the route because this
 * predicate also guards rows read back out of the database: an audience that
 * was legal when written stays legal, but one hand-edited into the table to ask
 * for a 3,000-day scan of the money table does not.
 */
export function isNoticeAudience(value: unknown): value is NoticeAudience {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as { type?: unknown; models?: unknown; window_days?: unknown };
  if (candidate.type !== 'models_used') return false;
  if (!Array.isArray(candidate.models) || candidate.models.length === 0) return false;
  for (const entry of candidate.models) {
    if (typeof entry !== 'object' || entry === null) return false;
    const ref = entry as { vendor?: unknown; model?: unknown };
    if (typeof ref.vendor !== 'string' || ref.vendor === '') return false;
    if (typeof ref.model !== 'string' || ref.model === '') return false;
  }
  const days = candidate.window_days;
  return typeof days === 'number'
    && Number.isInteger(days)
    && days > 0
    && days <= EMAIL_NOTICE_MAX_WINDOW_DAYS;
}

/** `YYYY-MM-DD`, and a real calendar date — `2026-02-31` is refused. */
export function isEmailNoticeRemindOn(value: unknown): value is string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}
